/**
 * The wide grids' per-column header menu: sort (Asc / Desc) and a filter that suits the
 * column's type, drafted until OK (C-293). Shared by Admin → Securities and Portfolios →
 * View portfolio, so the two grids sort and filter the same way (§3h, Pedro 2026-10-19).
 *
 * Filter shapes by column type:
 *   text → "contains" string · enum / lens → the ticked values · date → from/to · num → min/max.
 */
import { useState } from 'react';
import { Button, Group, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { IconSortAscending, IconSortDescending } from '@tabler/icons-react';

export type Ft = 'text' | 'enum' | 'lens' | 'date' | 'num';
export type FilterValue = string | string[] | { from: string; to: string } | { min: string; max: string } | undefined;
export type Filters = Record<string, FilterValue>;
export interface SortState { key: string; dir: 'asc' | 'desc' }

export function filterActive(col: { ft: Ft }, f: FilterValue): boolean {
  if (f == null) return false;
  if (col.ft === 'text') return f !== '';
  if (col.ft === 'enum' || col.ft === 'lens') return Array.isArray(f) && f.length > 0;
  if (col.ft === 'date') { const d = f as { from: string; to: string }; return !!(d.from || d.to); }
  const n = f as { min: string; max: string }; return !!(n.min || n.max);
}

/** Does a cell pass an active filter? `text` is what the cell shows; `num` its number. */
export function passesFilter(ft: Ft, f: FilterValue, text: string, num: number | null): boolean {
  if (ft === 'text') return text.toLowerCase().includes((f as string).toLowerCase());
  if (ft === 'enum' || ft === 'lens') return (f as string[]).includes(text);
  if (ft === 'date') {
    const { from, to } = f as { from: string; to: string };
    if (from && (!text || text < from)) return false;
    return !(to && (!text || text > to));
  }
  const { min, max } = f as { min: string; max: string };
  if (min !== '' && (num == null || num < Number(min))) return false;
  return !(max !== '' && (num == null || num > Number(max)));
}

function Checklist({ options, selected, onToggle }: { options: string[]; selected: string[]; onToggle: (v: string) => void }) {
  return (
    <ScrollArea.Autosize mah={220}>
      <Stack gap={0}>
        {options.length === 0 && <Text size="xs" c="dimmed" p={4}>No values.</Text>}
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <Group key={o} gap={8} wrap="nowrap" onClick={() => onToggle(o)}
              style={{ cursor: 'pointer', padding: '4px 4px', borderRadius: 4 }}>
              <span style={{ width: 14, textAlign: 'center', color: 'var(--mantine-color-azul-6)', opacity: on ? 1 : 0.15 }}>✓</span>
              <Text size="sm">{o}</Text>
            </Group>
          );
        })}
      </Stack>
    </ScrollArea.Autosize>
  );
}

export function ColumnMenu({
  col, options, initialFilter, sort, onSort, onApply, onClear,
}: {
  col: { key: string; ft: Ft }; options: string[]; initialFilter: FilterValue;
  sort: SortState | null;
  onSort: (dir: 'asc' | 'desc') => void;
  onApply: (draft: FilterValue) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = useState<FilterValue>(initialFilter);
  const d = draft as { from: string; to: string } & { min: string; max: string };

  return (
    <Stack gap="sm">
      <div>
        <Text size="xs" c="dimmed" tt="uppercase" mb={4}>Sort</Text>
        <Group gap={6} grow>
          <Button size="xs" variant={sort?.key === col.key && sort.dir === 'asc' ? 'filled' : 'default'}
            leftSection={<IconSortAscending size={14} />} onClick={() => onSort('asc')}>Asc</Button>
          <Button size="xs" variant={sort?.key === col.key && sort.dir === 'desc' ? 'filled' : 'default'}
            leftSection={<IconSortDescending size={14} />} onClick={() => onSort('desc')}>Desc</Button>
        </Group>
      </div>
      <div>
        <Text size="xs" c="dimmed" tt="uppercase" mb={4}>Filter</Text>
        {col.ft === 'text' && (
          <TextInput size="xs" placeholder="contains…" value={(draft as string) ?? ''}
            onChange={(e) => setDraft(e.currentTarget.value)} />
        )}
        {(col.ft === 'enum' || col.ft === 'lens') && (
          <Checklist options={options} selected={(draft as string[]) ?? []}
            onToggle={(v) => {
              const cur = (draft as string[]) ?? [];
              setDraft(cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
            }} />
        )}
        {col.ft === 'date' && (
          <Stack gap={6}>
            <TextInput size="xs" type="date" value={d?.from ?? ''}
              onChange={(e) => setDraft({ from: e.currentTarget.value, to: d?.to ?? '' })} />
            <TextInput size="xs" type="date" value={d?.to ?? ''}
              onChange={(e) => setDraft({ from: d?.from ?? '', to: e.currentTarget.value })} />
          </Stack>
        )}
        {col.ft === 'num' && (
          <Group gap={6} grow>
            <TextInput size="xs" type="number" placeholder="min" value={d?.min ?? ''}
              onChange={(e) => setDraft({ min: e.currentTarget.value, max: d?.max ?? '' })} />
            <TextInput size="xs" type="number" placeholder="max" value={d?.max ?? ''}
              onChange={(e) => setDraft({ min: d?.min ?? '', max: e.currentTarget.value })} />
          </Group>
        )}
      </div>
      <Group justify="flex-end" gap="xs">
        <Button size="xs" onClick={() => onApply(draft)}>OK</Button>
        <Button size="xs" variant="default" onClick={onClear}>Clear</Button>
      </Group>
    </Stack>
  );
}
